/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox の共有未配置トレイ（全チーム共通の全体プール）の純粋ロジック。実行時のブロック配列を「配置済み = チーム白板の持ち物」と「未配置 = 共有トレイの持ち物」へ振り分け、共有トレイへ入るブロックをチーム非依存の形（トレイ専用 ID・共有 teamKey・チーム固有の候補参照の除去）へ正規化する。共有トレイ文書の保存モデル ↔ 実行時モデルの変換もここに置く（配置を持たないため、チーム白板のようなメンバー行 / 日付への解決は不要）
 * @graph-connects none
 */
import {
  PJ_SANDBOX_SHARED_TRAY_KEY,
  type PjSandboxStoredBlock,
} from '@cortex/domain-product-innovation-lab-types';
import type { SandboxBlock } from './types.js';

/**
 * 共有トレイに居るブロックの `teamKey`。トレイはどのチームにも属さない全体プールなので、
 * 表示中のチームではなく固定値を持たせる（チームを切り替えても持ち主が変わらない）。
 * @graph-connects none
 */
export const SHARED_TRAY_TEAM_KEY: string = PJ_SANDBOX_SHARED_TRAY_KEY;

/**
 * 共有トレイのブロック ID に付ける prefix。
 *
 * チーム白板の ID は `block-<連番>` で、**その白板に現存する ID の最大値**から採番する
 * (`maxCounterFrom`)。共有トレイは白板とは別文書なので、トレイへ移したブロックの ID が
 * 白板側から消えると、白板が同じ連番を再発行してしまう。そのブロックを白板へ戻したときに
 * ID が衝突して別ブロックを踏み潰すため、トレイのブロックは連番と交わらない
 * 専用 prefix + ランダム ID で採番する。
 * @graph-connects none
 */
export const TRAY_BLOCK_ID_PREFIX = 'tray-';

/**
 * 共有トレイ用のブロック ID を払い出す。白板の連番とは別空間になるよう prefix を付ける。
 * @graph-connects none
 */
export function newTrayBlockId(): string {
  return `${TRAY_BLOCK_ID_PREFIX}${crypto.randomUUID()}`;
}

/**
 * チーム白板の文書に残っている未配置ブロックを共有トレイへ引き取るときの ID。
 *
 * `newTrayBlockId` の乱数と違い、**同じ白板の同じブロックからは常に同じ ID** になる。
 * 引き取りは白板を読み込むたびに走る一方、白板文書から取り除かれるのは次の保存が
 * 通ったあとなので、乱数で採番すると開き直すたびに別ブロックとして共有トレイへ
 * 積み上がってしまう（読み取り専用の閲覧者は保存が走らないため永遠に増える）。
 * @graph-connects none
 */
export function adoptedTrayBlockId(boardKey: string, blockId: string): string {
  return `${TRAY_BLOCK_ID_PREFIX}${boardKey}-${blockId}`;
}

/** ブロックを共有トレイの持ち物の形へ整える（ID は呼び出し側が決める）。 @graph-connects none */
function withTrayIdentity(block: SandboxBlock, id: string): SandboxBlock {
  return {
    id,
    name: block.name,
    sizePt: block.sizePt,
    kind: block.kind,
    teamKey: SHARED_TRAY_TEAM_KEY,
    placement: null,
    ...(block.issueKey === undefined ? {} : { issueKey: block.issueKey }),
  };
}

/**
 * ブロックを共有トレイの住人として正規化する。
 *
 * - ID がトレイ空間でなければ払い出し直す（白板の連番との衝突を避ける。上記 prefix の説明参照）
 * - `teamKey` を共有トレイの固定値にする
 * - `sourceCandidateId`（チーム白板の候補案件を指す参照）を捨てる。トレイは全チーム共通なので、
 *   別チームの白板を開いたときに同じ ID の候補を「配置済み」と誤表示させないため
 *
 * 既に正規化済みならそのまま返す（参照を保ち、不要な再描画と保存を起こさない）。
 * @graph-connects none
 */
export function toSharedTrayBlock(block: SandboxBlock): SandboxBlock {
  const normalized =
    block.id.startsWith(TRAY_BLOCK_ID_PREFIX) &&
    block.teamKey === SHARED_TRAY_TEAM_KEY &&
    block.sourceCandidateId === undefined;
  if (normalized) return block;

  return withTrayIdentity(
    block,
    block.id.startsWith(TRAY_BLOCK_ID_PREFIX) ? block.id : newTrayBlockId(),
  );
}

/**
 * チーム白板の文書に未配置のまま残っているブロックを、共有トレイの住人として引き取る。
 *
 * 共有トレイを分ける前に保存された未配置ブロックと、担当メンバーが名簿から消えて配置を
 * 失ったブロックがこれにあたる。どちらも「まだ誰の・いつの予定でもない案件」なので、
 * 表示中のチームに縛られた持ち物のままだとタブを切り替えた瞬間に画面から消える。
 * `toSharedTrayBlock` との違いは ID が決定的なことだけ（`adoptedTrayBlockId` 参照）。
 * @graph-connects adoptedTrayBlockId [calls] 引き取り後の決定的なトレイ ID の採番
 */
export function adoptTrayBlock(block: SandboxBlock, boardKey: string): SandboxBlock {
  return withTrayIdentity(
    block,
    block.id.startsWith(TRAY_BLOCK_ID_PREFIX) ? block.id : adoptedTrayBlockId(boardKey, block.id),
  );
}

/**
 * 引き取ったブロックを、いま共有トレイに居るブロックへ ID 重複なく足す。
 *
 * 引き取りは白板の読み込みごとに走り、共有トレイ文書の読み込みとも順序が定まらないため、
 * 同じブロックが二度流れてくる。足すものが無ければ現在の配列をそのまま返す
 * （参照を保ち、無駄な再描画と保存を起こさない）。
 * @graph-connects none
 */
export function mergeAdoptedTrayBlocks(
  current: SandboxBlock[],
  adopted: readonly SandboxBlock[],
): SandboxBlock[] {
  const known = new Set(current.map((block) => block.id));
  const added = adopted.filter((block) => !known.has(block.id));
  return added.length === 0 ? current : [...current, ...added];
}

/** ブロックを保存先ごとに振り分けた結果。 */
export interface PartitionedBlocks {
  /** 配置済み。表示中のチーム白板の文書へ保存する。 */
  placed: SandboxBlock[];
  /** 未配置。全チーム共有のトレイ文書へ保存する。 */
  tray: SandboxBlock[];
}

/**
 * 実行時のブロック配列を、保存先（チーム白板 / 共有トレイ）で振り分ける。
 *
 * - 未配置側は `toSharedTrayBlock` で共有トレイの住人として正規化する。
 * - 配置済み側は表示中のチーム白板の文書へ保存されるので `teamKey` を `teamKey`（表示中のチーム）へ
 *   揃える。共有トレイから白板へドラッグしたブロックは `teamKey` が共有トレイの固定値
 *   (`SHARED_TRAY_TEAM_KEY`) のままここへ来るため、持ち主（表示中のチーム）へ書き換えて
 *   `SandboxBlock.teamKey`（所属する白板の key）の契約を保つ。既に一致していれば参照を保つ
 *   （無駄な再描画と保存を起こさない）。
 *
 * @graph-connects toSharedTrayBlock [calls] 共有トレイへ入るブロックの正規化
 */
export function partitionBlocks(
  blocks: readonly SandboxBlock[],
  teamKey: string,
): PartitionedBlocks {
  const placed: SandboxBlock[] = [];
  const tray: SandboxBlock[] = [];
  for (const block of blocks) {
    if (block.placement === null) tray.push(toSharedTrayBlock(block));
    else placed.push(block.teamKey === teamKey ? block : { ...block, teamKey });
  }
  return { placed, tray };
}

/**
 * 共有トレイ文書の保存モデルを実行時モデルへ解決する。配置を持たないので、チーム白板の
 * `resolveStoredBoard` と違いメンバー行 / 表示期間への解決は要らない。
 * 万一 placement を持つ文書があっても未配置として読む（トレイの不変条件を守る）。
 * @graph-connects none
 */
export function fromStoredTrayBlocks(stored: readonly PjSandboxStoredBlock[]): SandboxBlock[] {
  return stored.map((block) => ({
    id: block.id,
    name: block.name,
    sizePt: block.sizePt,
    kind: block.kind,
    teamKey: SHARED_TRAY_TEAM_KEY,
    placement: null,
    ...(block.issueKey === undefined ? {} : { issueKey: block.issueKey }),
  }));
}

/**
 * 実行時の共有トレイブロックを保存モデルへ変換する。
 * @graph-connects none
 */
export function toStoredTrayBlocks(blocks: readonly SandboxBlock[]): PjSandboxStoredBlock[] {
  return blocks.map((block) => ({
    id: block.id,
    name: block.name,
    sizePt: block.sizePt,
    kind: block.kind,
    placement: null,
    ...(block.issueKey === undefined ? {} : { issueKey: block.issueKey }),
  }));
}
